import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRotateRight,
  faCheck,
  faCircleExclamation,
  faEye,
  faMagnifyingGlass,
  faShieldHalved,
  faUserCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";
import "./AdminFluxopayPayoutReviews.css";

import PayoutAuditTimeline from "../../components/fluxopay/PayoutAuditTimeline";

const money = (cents = 0) => new Intl.NumberFormat("pt-BR", {
  style: "currency", currency: "BRL",
}).format(Number(cents || 0) / 100);

const dateTime = (value) => value
  ? new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  : "—";

const statusLabel = {
  requested: "Solicitado", processing: "Processando", blocked: "Bloqueado",
  paid: "Pago", failed: "Falhou", cancelled: "Cancelado",
};

const reviewLabel = { open: "Aberto", in_progress: "Em análise", resolved: "Resolvido" };

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export default function AdminFluxopayPayoutReviews() {
  const [payload, setPayload] = useState({ summary: {}, payouts: { data: [] } });
  const [filters, setFilters] = useState({ search: "", review_status: "open", status: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState("confirmed_external");
  const [note, setNote] = useState("");
  const [password, setPassword] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const response = await api.get("/api/admin/fluxopay/payout-reviews", { params });
      setPayload(response.data);
    } catch (requestError) {
      setError(errorMessage(requestError, "Não foi possível carregar as pendências de repasse."));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { const timer = setTimeout(load, 250); return () => clearTimeout(timer); }, [load]);

  const action = async (row, type) => {
    setBusy(`${type}-${row.id}`); setError(""); setNotice("");
    try {
      const response = await api.post(`/api/admin/fluxopay/payout-reviews/${row.id}/${type}`);
      setNotice(response.data.message);
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError, "Não foi possível executar a ação."));
    } finally { setBusy(null); }
  };

  const openDetails = async (row) => {
    setBusy(`show-${row.id}`); setError("");
    try {
      const response = await api.get(`/api/admin/fluxopay/payout-reviews/${row.id}`);
      setSelected(response.data.payout); setNote(""); setPassword("");
    } catch (requestError) {
      setError(errorMessage(requestError, "Não foi possível abrir a revisão."));
    } finally { setBusy(null); }
  };

  const resolve = async (event) => {
    event.preventDefault(); setBusy(`resolve-${selected.id}`); setError(""); setNotice("");
    try {
      const response = await api.post(`/api/admin/fluxopay/payout-reviews/${selected.id}/resolve`, {
        resolution, note, password,
      });
      setNotice(response.data.message); setSelected(null); setPassword(""); setNote(""); await load();
    } catch (requestError) {
      setError(errorMessage(requestError, "Não foi possível encerrar a revisão."));
    } finally { setBusy(null); }
  };

  const summary = payload.summary || {};
  const rows = payload.payouts?.data || [];

  return <div className="afpr">
    <section className="afpr-summary">
      <article><span>Pendências operacionais</span><strong>{summary.total || 0}</strong><small>Repasses que exigem atenção</small></article>
      <article className="is-warning"><span>Abertas</span><strong>{summary.open || 0}</strong><small>Aguardando responsável</small></article>
      <article className="is-progress"><span>Em análise</span><strong>{summary.in_progress || 0}</strong><small>Assumidas pela equipe</small></article>
      <article className="is-danger"><span>Divergências</span><strong>{summary.divergent || 0}</strong><small>Valor local diferente do Asaas</small></article>
    </section>

    <section className="afpr-panel">
      <header><div><small>CONTROLE OPERACIONAL</small><h3>Revisão de repasses</h3><p>Consulte o Asaas e registre a decisão sem alterar silenciosamente o resultado financeiro.</p></div><button onClick={load} disabled={loading}><FontAwesomeIcon icon={faArrowRotateRight} /> Atualizar</button></header>
      <div className="afpr-filters">
        <label><FontAwesomeIcon icon={faMagnifyingGlass} /><input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Clínica, código ou ID Asaas" /></label>
        <select value={filters.review_status} onChange={(event) => setFilters((current) => ({ ...current, review_status: event.target.value }))}><option value="">Todas as análises</option><option value="open">Abertas</option><option value="in_progress">Em análise</option><option value="resolved">Resolvidas</option></select>
        <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}><option value="">Todos os status</option><option value="processing">Processando</option><option value="blocked">Bloqueado</option><option value="failed">Falhou</option><option value="paid">Pago</option></select>
      </div>
      {error && <div className="afpr-alert is-error"><FontAwesomeIcon icon={faCircleExclamation} /> {error}</div>}
      {notice && <div className="afpr-alert is-success"><FontAwesomeIcon icon={faCheck} /> {notice}</div>}
      <div className="afpr-table-wrap"><table><thead><tr><th>Clínica / repasse</th><th>Valor</th><th>Status</th><th>Conciliação</th><th>Tentativas</th><th>Última tentativa</th><th>Análise</th><th>Ações</th></tr></thead><tbody>
        {loading ? <tr><td colSpan="8" className="afpr-empty">Carregando pendências...</td></tr> : rows.length === 0 ? <tr><td colSpan="8" className="afpr-empty">Nenhum repasse encontrado para estes filtros.</td></tr> : rows.map((row) => {
          const review = row.review_status || "open";
          return <tr key={row.id}>
            <td><strong>{row.entidade?.nome || "Clínica não identificada"}</strong><small>{row.uuid?.slice(0, 8)} · {row.provider_payout_id || "Sem ID Asaas"}</small></td>
            <td><strong>{money(row.amount_cents)}</strong><small>{dateTime(row.created_at)}</small></td>
            <td><span className={`afpr-badge is-${row.status}`}>{statusLabel[row.status] || row.status}</span></td>
            <td><span className={`afpr-badge is-${row.reconciliation_status}`}>{row.reconciliation_status}</span></td>
            <td>{row.sync_attempts || 0}</td><td>{dateTime(row.last_sync_attempt_at)}</td>
            <td><span className={`afpr-badge is-${review}`}>{reviewLabel[review]}</span></td>
            <td><div className="afpr-actions"><button title="Ver detalhes" disabled={!!busy} onClick={() => openDetails(row)}><FontAwesomeIcon icon={faEye} /></button><button title="Assumir análise" disabled={!!busy || review !== "open"} onClick={() => action(row, "claim")}><FontAwesomeIcon icon={faUserCheck} /></button><button title="Consultar no Asaas" disabled={!!busy || !row.provider_payout_id} onClick={() => action(row, "sync")}><FontAwesomeIcon icon={faArrowRotateRight} /></button></div></td>
          </tr>;
        })}
      </tbody></table></div>
    </section>

    {selected && <div className="afpr-backdrop" onMouseDown={() => setSelected(null)}><form className="afpr-modal" onSubmit={resolve} onMouseDown={(event) => event.stopPropagation()}><header><div><small>REVISÃO FINANCEIRA</small><h3>Repasse {selected.uuid?.slice(0, 8)}</h3></div><button type="button" onClick={() => setSelected(null)}><FontAwesomeIcon icon={faXmark} /></button></header><div className="afpr-proof"><FontAwesomeIcon icon={faShieldHalved} /><div><strong>Dados preservados para auditoria</strong><p>Status {statusLabel[selected.status] || selected.status}, valor {money(selected.amount_cents)} e ID Asaas {selected.provider_payout_id || "não retornado"}.</p></div></div><dl><div><dt>Clínica</dt><dd>{selected.entidade?.nome}</dd></div><div><dt>Conciliação</dt><dd>{selected.reconciliation_status}</dd></div><div><dt>Erro de sincronização</dt><dd>{selected.sync_error || selected.failure_reason || "Não informado"}</dd></div><div><dt>Responsável atual</dt><dd>{selected.reviewer?.nome || "Não atribuído"}</dd></div></dl>
    <PayoutAuditTimeline
      adminPayoutId={selected.id}
    />
    <label>Conclusão da análise<select value={resolution} onChange={(event) => setResolution(event.target.value)}><option value="confirmed_external">Confirmado externamente</option><option value="cancelled_external">Cancelado externamente</option><option value="false_positive">Falso positivo operacional</option></select></label><label>Justificativa<textarea minLength="10" maxLength="2000" required value={note} onChange={(event) => setNote(event.target.value)} placeholder="Informe onde e como a situação foi confirmada." /></label><label>Confirme sua senha<input type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label><footer><button type="button" className="is-secondary" onClick={() => setSelected(null)}>Cancelar</button><button type="submit" disabled={busy === `resolve-${selected.id}` || note.length < 10 || !password}>Encerrar revisão</button></footer></form></div>}
  </div>;
}
